import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Store } from 'lucide-react';
import { Button } from '@/shared/components/Button';
import { useAuth } from '@/shared/hooks/useAuth';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { refreshAccessToken } from '@/shared/lib/auth-coordinator';
import styles from './ErrorPage.module.css';

/**
 * Tela da loja desativada pela equipe Vapt Menu.
 *
 * Separada da tela de assinatura de propósito: lá o caminho é assinar um
 * plano, e pagar não reativa uma loja desativada. Aqui o caminho é o suporte.
 *
 * O token que trouxe o dono até aqui diz `deactivated` até vencer, e esta
 * tela não chama a API — sem o "Verificar de novo", quem fosse reativado
 * ficaria preso aqui até sair e entrar.
 */
export function StoreDeactivatedPage() {
  const { tenants, logout } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [stillDeactivated, setStillDeactivated] = useState(false);

  async function checkAgain() {
    setChecking(true);
    setStillDeactivated(false);
    try {
      await refreshAccessToken();
    } catch {
      // A renovação que falha já encerrou a sessão. Sem refresh token, ela só
      // limpa a sessão e não sai da tela — daí a ida explícita à entrada.
      navigate('/login', { replace: true });
      return;
    } finally {
      setChecking(false);
    }

    if (useAuthStore.getState().user?.subscriptionStatus === 'deactivated') {
      setStillDeactivated(true);
      return;
    }

    navigate('/', { replace: true });
  }

  return (
    <div className={styles.wrapper}>
      <Store size={64} strokeWidth={1.5} className={styles.icon} />
      <h1 className={styles.code}>Loja</h1>
      <p className={styles.title}>Loja desativada</p>
      <p className={styles.description}>
        A equipe Vapt Menu desativou esta loja. O cardápio está fora do ar e o
        painel fica bloqueado até a reativação. Fale com o suporte para entender
        o motivo.
      </p>
      {stillDeactivated && (
        <p className={styles.description} role="status">
          A loja continua desativada.
        </p>
      )}
      <div className={styles.actions}>
        <Button variant="primary" loading={checking} onClick={checkAgain}>
          Verificar de novo
        </Button>
        {tenants.length > 1 && (
          <Button variant="secondary" onClick={() => navigate('/select-tenant')}>
            Trocar de loja
          </Button>
        )}
        <Button variant="ghost" onClick={logout}>
          Sair
        </Button>
      </div>
    </div>
  );
}
